import { MemberHome } from "./home/MemberHome";
import { useCallback, useEffect, useState, type ReactNode } from "react";
import { Link, useParams } from "react-router";
import { useAuth } from "@/auth/AuthContext";
import { Avatar } from "@/components/Avatar";
import { Chip } from "@/components/Chip";
import { useApi } from "@/lib/api";
import { hasRole } from "@/lib/hasRole";
import { formatDateTime } from "@/lib/formatDateTime";
import { formatDuration } from "@/lib/formatDuration";
import { formatRelativeTime } from "@/lib/formatRelativeTime";
import { plural } from "@/lib/plural";
import {
  attentionDetail,
  attentionSummary,
  eventsPath,
  overviewPath,
  recordingsPath,
  recordingType,
  resourcePath,
  type EventsResult,
  type Overview,
  type Recording,
} from "./home/shared";
import { HostIdentityBadge } from "@/components/HostIdentityBadge";
import { ResourceStatusPill } from "./resources/ResourceRouteCell";
import { isRouteState } from "./resources/shared";
import { LoadError } from "@/components/LoadError";
import { EmptyState } from "@/components/EmptyState";

// The dashboard summarizes rather than tracks, so it refreshes at half the
// rate of the console's live pages.
const REFRESH_MS = 30_000;

const linkClass = "text-sm text-accent hover:underline";

/** null while loading, "failed" when the request failed, otherwise the value. */
type Region<T> = T | null | "failed";

function useRegion<T>(path: string, pollMs?: number): [Region<T>, () => void] {
  const api = useApi();
  const [state, setState] = useState<Region<T>>(null);
  const [attempt, setAttempt] = useState(0);

  const retry = useCallback(() => {
    setState(null);
    setAttempt((previous) => previous + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;

    function load() {
      void api.get<T>(path).then((data) => {
        if (!cancelled) {
          setState(data ?? "failed");
        }
      });
    }

    load();

    const interval = pollMs ? setInterval(load, pollMs) : undefined;

    return () => {
      cancelled = true;

      if (interval) {
        clearInterval(interval);
      }
    };
  }, [api, path, pollMs, attempt]);

  return [state, retry];
}

function Section({
  title,
  to,
  action,
  className = "",
  children,
}: {
  title: string;
  to?: string;
  action?: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <section className={`min-w-0 ${className}`}>
      <div className="mb-3 flex items-center justify-between gap-3">
        <h2 className="text-sm font-semibold">{title}</h2>
        {to && action && (
          <Link to={to} className={linkClass}>
            {action}
          </Link>
        )}
      </div>
      {children}
    </section>
  );
}

const rowClass = "h-14 border-b border-surface/70 last:border-b-0";

const cellClass = "py-2 pr-4 align-middle last:pr-0";

function Columns({ labels }: { labels: string[] }) {
  return (
    <thead>
      <tr>
        {labels.map((label) => (
          <th
            key={label}
            scope="col"
            className="border-b border-surface pb-2.5 pr-4 text-xs font-semibold uppercase tracking-wider text-foreground/70 last:pr-0"
          >
            {label}
          </th>
        ))}
      </tr>
    </thead>
  );
}

function Person({
  name,
  email,
  avatarId,
  fallback,
}: {
  name: string | null;
  email: string | null;
  avatarId: string | null;
  fallback: string;
}) {
  if (!name && !email) {
    return <span className="text-foreground/70">{fallback}</span>;
  }

  return (
    <div className="flex items-center gap-2">
      <Avatar name={name} email={email} avatarId={avatarId} size="md" />
      <span className="text-foreground/70">{name ?? email}</span>
    </div>
  );
}

function SkeletonRows({ columns }: { columns: number }) {
  return (
    <>
      {Array.from({ length: 4 }, (_, row) => (
        <tr key={row} className={`${rowClass} animate-pulse`}>
          {Array.from({ length: columns }, (_, cell) => (
            <td key={cell} className={cellClass}>
              <div className="h-3 w-20 rounded bg-muted/40" />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

function Header({ children }: { children?: ReactNode }) {
  return (
    <div className="mb-4">
      <h1 className="text-2xl font-semibold tracking-tight">Dashboard</h1>
      {children && (
        <p className="mt-0.5 text-sm text-foreground/70">{children}</p>
      )}
    </div>
  );
}

function Panel({ children }: { children: ReactNode }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-muted bg-surface/40 px-4 pt-3">
      <table className="w-full text-left text-sm">{children}</table>
    </div>
  );
}

function Placeholder({ className = "" }: { className?: string }) {
  return (
    <div
      role="status"
      aria-label="Loading"
      className={`animate-pulse rounded-lg border border-muted bg-surface/40 ${className}`}
    />
  );
}

function Card({
  title,
  to,
  count,
  children,
}: {
  title: string;
  to: string;
  count: number;
  children?: ReactNode;
}) {
  return (
    <Link
      to={to}
      className="rounded-lg border border-muted bg-surface/40 p-4 hover:border-accent"
    >
      <p className="text-sm text-foreground/70">{title}</p>
      <p className="my-1 text-3xl font-medium tabular-nums">{count}</p>
      {children && <p className="text-xs">{children}</p>}
    </Link>
  );
}

function AttentionBadge({ reason }: { reason: string }) {
  if (reason === "SessionsRefused") {
    return <HostIdentityBadge state="AwaitingApproval" />;
  }

  if (isRouteState(reason)) {
    return <ResourceStatusPill state={reason} />;
  }

  return <Chip variant="warning">Needs attention</Chip>;
}

function Health({
  count,
  ok,
  problem,
  tone = "danger",
}: {
  count: number;
  ok: string;
  problem: string;
  tone?: "danger" | "warning";
}) {
  const color =
    count === 0
      ? "text-success"
      : tone === "warning"
        ? "text-warning"
        : "text-danger";

  return (
    <span className={`inline-flex items-center gap-1.5 ${color}`}>
      <span
        aria-hidden="true"
        className="h-1.5 w-1.5 rounded-full bg-current"
      />
      {count === 0 ? ok : problem}
    </span>
  );
}

function AdminHome({ orgId }: { orgId: string }) {
  const base = `/${orgId}`;
  const [overview, retryOverview] = useRegion<Overview>(
    overviewPath(orgId),
    REFRESH_MS,
  );
  const [events, retryEvents] = useRegion<EventsResult>(
    eventsPath(orgId),
    REFRESH_MS,
  );
  const [recordings, retryRecordings] = useRegion<Recording[]>(
    recordingsPath(orgId),
    REFRESH_MS,
  );

  const attention =
    overview && overview !== "failed" ? overview.resources : null;
  const attentionCount =
    overview && overview !== "failed" ? overview.resourcesNeedingAttention : 0;

  // Every region failing means the API is unreachable, not one bad endpoint:
  // say so once rather than stacking a banner on each section.
  const offline =
    overview === "failed" && events === "failed" && recordings === "failed";

  if (offline) {
    return (
      <>
        <Header />
        <LoadError
          what="the dashboard"
          onRetry={() => {
            retryOverview();
            retryEvents();
            retryRecordings();
          }}
        />
      </>
    );
  }

  return (
    <>
      <Header />

      {overview === "failed" ? (
        <LoadError
          what="the dashboard"
          onRetry={retryOverview}
          className="mb-6"
        />
      ) : (
        <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          {overview === null ? (
            [0, 1, 2, 3, 4].map((i) => (
              <Placeholder key={i} className="h-[104px]" />
            ))
          ) : (
            <>
              <Card
                title="Users"
                to={`${base}/users`}
                count={overview.counts.users}
              >
                <Health
                  count={overview.counts.usersPendingInvites}
                  ok="No pending invites"
                  problem={plural(
                    overview.counts.usersPendingInvites,
                    "pending invite",
                  )}
                  tone="warning"
                />
              </Card>
              <Card
                title="Teams"
                to={`${base}/teams`}
                count={overview.counts.teams}
              >
                <Health
                  count={overview.counts.teamsWithoutRules}
                  ok="All have access rules"
                  problem={`${overview.counts.teamsWithoutRules} without rules`}
                  tone="warning"
                />
              </Card>
              <Card
                title="Gateways"
                to={`${base}/networking/gateways`}
                count={overview.counts.gateways}
              >
                <Health
                  count={overview.counts.gatewaysOffline}
                  ok="All online"
                  problem={`${plural(overview.counts.gatewaysOffline, "gateway")} offline`}
                />
              </Card>
              <Card
                title="Connectors"
                to={`${base}/networking/connectors`}
                count={overview.counts.connectors}
              >
                <Health
                  count={overview.counts.connectorsOffline}
                  ok="All online"
                  problem={`${plural(overview.counts.connectorsOffline, "connector")} offline`}
                />
              </Card>
              <Card
                title="Resources"
                to={`${base}/resources`}
                count={overview.counts.resources}
              >
                <Health
                  count={attentionCount}
                  ok="All reachable"
                  problem={attentionSummary(attentionCount)}
                />
              </Card>
            </>
          )}
        </div>
      )}

      {overview !== "failed" && (
        <Section
          title="Resources needing attention"
          to={`${base}/resources`}
          className="mb-6"
          action="View resources"
        >
          {attention === null ? (
            <Panel>
              <Columns labels={["Resource", "Issue", "Next step"]} />
              <tbody>
                <SkeletonRows columns={3} />
              </tbody>
            </Panel>
          ) : !attention.length ? (
            <EmptyState
              title="No resources need attention"
              description="All resources are reachable and have approved host identities."
            />
          ) : (
            <Panel>
              <Columns labels={["Resource", "Issue", "Next step"]} />
              <tbody>
                {attention.map((item) => (
                  <tr key={`${item.id}-${item.reason}`} className={rowClass}>
                    <td className={cellClass}>
                      <Link
                        to={`${base}${resourcePath(item.type)}`}
                        className="break-words text-accent hover:underline"
                      >
                        {item.name}
                      </Link>
                    </td>
                    <td className={cellClass}>
                      <AttentionBadge reason={item.reason} />
                    </td>
                    <td className={cellClass}>
                      {attentionDetail(item.reason)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Panel>
          )}
        </Section>
      )}

      <div className="mb-6 grid gap-6 xl:grid-cols-2">
        <Section
          title="Recent audit logs"
          to={`${base}/logs/audit`}
          action="View audit logs"
        >
          {events === null ? (
            <Panel>
              <Columns labels={["Change", "User", "When"]} />
              <tbody>
                <SkeletonRows columns={3} />
              </tbody>
            </Panel>
          ) : events === "failed" ? (
            <LoadError what="audit logs" onRetry={retryEvents} />
          ) : !events.items.length ? (
            <EmptyState
              title="No activity yet"
              description="Changes to users, teams, resources, and sessions appear here."
            />
          ) : (
            <Panel>
              <Columns labels={["Change", "User", "When"]} />
              <tbody>
                {events.items.map((event) => (
                  <tr key={event.id} className={rowClass}>
                    <td className={`${cellClass} break-words`}>
                      {event.description}
                    </td>
                    <td className={cellClass}>
                      <Person
                        name={event.userName}
                        email={event.userEmail}
                        avatarId={event.userAvatarId}
                        fallback="System"
                      />
                    </td>
                    <td className={`${cellClass} whitespace-nowrap`}>
                      <time
                        dateTime={event.createdAt}
                        title={formatDateTime(event.createdAt)}
                      >
                        {formatRelativeTime(event.createdAt)}
                      </time>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Panel>
          )}
        </Section>

        <Section
          title="Recent recordings"
          to={`${base}/logs/ssh-recordings`}
          action="View recordings"
        >
          {recordings === null ? (
            <Panel>
              <Columns
                labels={[
                  "Resource",
                  "User",
                  "Protocol",
                  "Duration",
                  "Recorded",
                ]}
              />
              <tbody>
                <SkeletonRows columns={5} />
              </tbody>
            </Panel>
          ) : recordings === "failed" ? (
            <LoadError what="recordings" onRetry={retryRecordings} />
          ) : !recordings.length ? (
            <EmptyState
              title="No recordings yet"
              description="Recorded sessions will appear here after people connect to resources."
            />
          ) : (
            <Panel>
              <Columns
                labels={[
                  "Resource",
                  "User",
                  "Protocol",
                  "Duration",
                  "Recorded",
                ]}
              />
              <tbody>
                {recordings.map((recording) => (
                  <tr key={recording.id} className={rowClass}>
                    <td className={cellClass}>
                      <Link
                        className="text-accent hover:underline"
                        to={`${base}/logs/${recordingType(recording.connectionMethod).path}`}
                      >
                        {recording.resourceName}
                      </Link>
                    </td>
                    <td className={cellClass}>
                      <Person
                        name={recording.requestedByUserName}
                        email={recording.requestedByUserEmail}
                        avatarId={recording.requestedByUserAvatarId}
                        fallback="Unknown"
                      />
                    </td>
                    <td className={cellClass}>
                      <Chip>
                        {recordingType(recording.connectionMethod).label}
                      </Chip>
                    </td>
                    <td
                      className={`${cellClass} whitespace-nowrap tabular-nums`}
                    >
                      {formatDuration(recording.durationSeconds)}
                    </td>
                    <td
                      className={`${cellClass} whitespace-nowrap tabular-nums`}
                    >
                      <time
                        dateTime={recording.requestedAt}
                        title={formatDateTime(recording.requestedAt)}
                      >
                        {formatRelativeTime(recording.requestedAt)}
                      </time>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Panel>
          )}
        </Section>
      </div>
    </>
  );
}

export function Home() {
  const { user } = useAuth();
  const { orgId } = useParams();

  return (
    <div className="text-foreground">
      {hasRole(user?.roles, "Admin") ? (
        <AdminHome orgId={orgId ?? ""} />
      ) : (
        <MemberHome />
      )}
    </div>
  );
}
